import { useEffect, useState } from "react";
import type { Location } from "@/types";
import { CheckIcon, PinIcon } from "@/app/components/system/Icons";
import { POPULAR_CITIES, detectLocation, searchCities } from "@/app/lib/weather";

interface LocationPickerProps {
  current: Location | null;
  onPick: (loc: Location) => void;
}

/**
 * Inline city search and geolocation picker — same behavior as LocationModal
 * without the modal shell, for onboarding and other full-page flows.
 */
export function LocationPicker({ current, onPick }: Readonly<LocationPickerProps>) {
  const [query, setQuery] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [results, setResults] = useState<Location[]>(POPULAR_CITIES);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(POPULAR_CITIES);
      setError(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      searchCities(q)
        .then((cities) => { if (!cancelled) { setResults(cities); setError(null); } })
        .catch((err: Error) => { if (!cancelled) { setError(err.message || "Search failed"); setResults([]); } })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const detect = async () => {
    setDetecting(true);
    setError(null);
    try {
      const loc = await detectLocation();
      onPick(loc);
    } catch (err) {
      setError((err as Error).message || "Could not detect location");
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={detect}
        disabled={detecting}
        className="flex items-center gap-2.5 w-full px-3.5 py-3 rounded-2xl bg-moss-soft text-moss font-sans text-sm font-medium cursor-pointer tracking-[0.005em] border hover:bg-[oklch(from_var(--moss-soft)_calc(l-0.04)_c_h)] disabled:opacity-70 disabled:cursor-progress"
        style={{ borderColor: "oklch(from var(--moss) calc(l + 0.18) c h)" }}
      >
        <PinIcon size={14} />
        <span>{detecting ? "Finding your spot…" : "Use my current location"}</span>
        {detecting && (
          <span
            className="w-3.5 h-3.5 rounded-full ml-auto border-2 animate-[loc-spin_0.7s_linear_infinite]"
            style={{ borderColor: "oklch(from var(--moss) calc(l + 0.2) c h)", borderTopColor: "var(--moss)" }}
          />
        )}
      </button>

      <div className="flex items-center gap-3 my-0.5 font-sans text-[11px] font-medium text-ink-3 uppercase tracking-[0.08em]">
        <span aria-hidden="true" className="flex-1 h-px bg-line" />
        <span>or search</span>
        <span aria-hidden="true" className="flex-1 h-px bg-line" />
      </div>

      <input
        className="field-input-bare px-3.5 py-3"
        placeholder="City, region…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <ul className="list-none m-0 p-0 overflow-y-auto flex flex-col gap-1 max-h-52">
        {error && <li className="p-4 text-center text-ink-3 font-sans text-[13px] italic">{error}</li>}
        {!error && searching && <li className="p-4 text-center text-ink-3 font-sans text-[13px] italic">Searching…</li>}
        {!error && !searching && results.length === 0 && (
          <li className="p-4 text-center text-ink-3 font-sans text-[13px] italic">No matches. Try another spelling.</li>
        )}
        {!error && !searching && results.map((c) => {
          const isCurrent = !!(current && current.id === c.id);
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onPick(c)}
                className={`grid items-center gap-2.5 grid-cols-[auto_1fr_auto_auto] w-full px-3 py-2.5 rounded-xl border text-left cursor-pointer font-sans text-sm transition-colors duration-150 ${
                  isCurrent
                    ? "bg-moss-soft text-moss [&_svg]:text-moss"
                    : "bg-transparent border-transparent text-ink hover:bg-paper hover:border-line"
                }`}
                style={isCurrent ? { borderColor: "oklch(from var(--moss) calc(l + 0.2) c h)" } : undefined}
              >
                <PinIcon size={13} />
                <span className="font-medium">{c.name}</span>
                <span className="font-sans text-xs text-ink-3 whitespace-nowrap">{c.region}</span>
                {isCurrent && <CheckIcon size={14} />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
